import { useState, useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { getCurrentOrigin } from '../../routing/helpers';
import { blacklistedSignupPaths, PROD_HOST } from '../constants';

export function isValidRedirectPath(currentPath: string) {
  const matches = blacklistedSignupPaths.filter((path) =>
    currentPath.match(path)
  );
  return matches.length === 0;
}

export function useSignup() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect');
  const currentPath =
    pathname && searchParams.size
      ? `${pathname}?${searchParams}`
      : pathname || '/';
  // We need to use the production host as the initial value for server side rendering as window.location.origin is not available
  const signupHost = `${PROD_HOST}/signup/`;
  const [signupUrl, setSignupUrl] = useState(signupHost);

  const redirectPath = (() => {
    if (redirect) {
      return redirect;
    }
    return currentPath !== '/' && isValidRedirectPath(currentPath)
      ? currentPath
      : '';
  })();

  useEffect(() => {
    const url = new URL(signupHost);

    if (redirectPath) {
      url.searchParams.append('redirect', redirectPath);
    }

    if (!PROD_HOST) {
      return;
    }
    // updating the url to contain the current domain name
    try {
      const currentOrigin = getCurrentOrigin();
      if (typeof currentOrigin === 'string' && typeof PROD_HOST === 'string') {
        const isProd = currentOrigin.includes(PROD_HOST);
        setSignupUrl(
          isProd ? url.href : url.href.replace(PROD_HOST, currentOrigin)
        );
      } else {
        setSignupUrl(url.href);
      }
    } catch {
      // Fallback if getCurrentOrigin fails in test environments
      setSignupUrl(url.href);
    }
  }, [redirectPath, signupHost]);

  return { url: signupUrl };
}
